<template>
    <record-editor
        id="user-editor"
        :record="edited_record"
        :title="page_title"
        left-panel-class="uk-width-1-1"
        :highlight-color="top_bar_color"
        :can-view-data="can_view_record_data"
        :can-edit-data="can_edit_record_data"
        :can-delete-data="can_delete_data"
        @remove="confirm_record_delete"
    >
        <template v-slot:page-title>
            <h2 v-if="!!edited_record" class="uk-flex uk-flex-middle uk-margin-bottom-remove lam-nowrap-ellipsis project-title">
                <span v-if="!!edited_record.number && !edited_record.internal" class="uk-flex-item-none uk-flex lam-title-separator uk-margin-small-right">
                    <span>{{ edited_record.number }}</span><!--
                    //--><span>.</span><!--
                    //--><input
                        id="number_suffix"
                        :value="edited_record.number_suffix"
                        name="number_suffix"
                        :required="false"
                        :disabled="!is_db_admin_active"
                        size="2"
                        class="uk-form-small"
                        @blur="change_suffix"
                    />
                </span>
                <span v-if="!!edited_record.name">{{ edited_record.name }}</span>
                <span v-else>Project</span>
                <a
                    v-if="!!edited_record.activated_at && !!edited_record.open_asset_url"
                    :href="edited_record.open_asset_url"
                    title="View on OpenAsset"
                    target="_blank"
                    style="padding: 0 6px; line-height: 24px; min-height: 24px;"
                    class="uk-button uk-button-link uk-button-white uk-button-medium uk-margin-left"
                >
                    <img src="/img/openasset.png" style="position: relative; top: -1px; max-height: 1.5em;" />
                    <open-icon glyph="chevron-right" class="uk-margin-small-left" />
                </a>
                <base-button
                    v-else-if="!!edited_record.activated_at && is_db_admin_active"
                    color="white"
                    title="Create on OpenAsset"
                    :waiting="linking_open_asset"
                    class="uk-margin-left"
                    style="padding: 0 6px; line-height: 24px; min-height: 24px;"
                    @click.prevent="link_open_asset"
                >
                    <img src="/img/openasset-disabled.png" style="position: relative; top: -1px; max-height: 1.5em;"  />
                </base-button>
            </h2>
            <div v-if="!!edited_record" class="lam-project-contacts">
                <span v-if="!!main_firm">
                    <router-link :to="{name: 'firm.edit', params: {id: main_firm.id}}">
                        {{ main_firm.name }}
                    </router-link>
                </span>
                <span v-if="!!main_firm && (!!pic || !!pm)">&nbsp;&bull;&nbsp;</span>
                <span v-if="!!pic" class="uk-margin-right uk-text-small">
                    PIC:
                    <strong>
                        <router-link :to="{name: 'user.edit', params: {id: pic.id}}">{{ pic.initials }}</router-link>
                    </strong>
                </span>
                <span v-if="!!pm" class="uk-margin-right uk-text-small">
                    PM:
                    <strong>
                        <router-link :to="{name: 'user.edit', params: {id: pm.id}}">{{ pm.initials }}</router-link>
                    </strong>
                </span>
                <dropdown-button
                    v-if="related_projects.length"
                    color="secondary"
                    size="small"
                    class="lam-related-projects-button"
                >
                    <template v-slot:dropdown>
                        <ul class="uk-nav uk-nav-dropdown">
                            <li
                                v-for="project in related_projects"
                                :key="project.id"
                            >
                                <router-link :to="{name: 'project.edit', params: {id: project.id}}">
                                    {{ project.name }}
                                </router-link>
                            </li>
                        </ul>
                    </template>
                    <open-icon glyph="random" />
                </dropdown-button>
            </div>
        </template>
        <template v-slot:title-view>
            <div v-if="!!edited_record&&!is_active">
                <strong
                    class="uk-button uk-button-large uk-inert-button uk-width-1-1"
                    :class="{
                        'uk-button-primary': ((!is_active && (edited_record.project_status === 'other')) || (is_active && (edited_record.project_status === 'on-hold'))),
                        'uk-button-danger': (!is_active && edited_record.shelf_life && edited_record.shelf_life.match(/(90\+\sdays|Dead)/i)) || (is_active && edited_record.project_status && edited_record.project_status.match(/^(dead|declined)$/i)),
                        'uk-button-warn': (!is_active && edited_record.shelf_life && edited_record.shelf_life.match(/(30|60)\-(60|90)\sdays/i)),
                        'uk-button-success': (!is_active && ((edited_record.shelf_life && (edited_record.shelf_life === '0-30 days')) || (edited_record.project_status && edited_record.project_status.match(/^(chosen\-pending)$/i))))
                    }"
                >
                    <span v-if="(edited_record.project_status && ((!is_active && !!edited_record.project_status.match(/^(other)$/i)) || !!edited_record.project_status.match(/^(chosen\-pending)$/i)))">
                        {{ project_status_label(edited_record.project_status) }}
                    </span>
                    <span v-else>{{ edited_record.shelf_life }}</span>
                </strong>
            </div>
            <div v-else-if="!!edited_record" class="uk-flex" style="font-size: 13px; line-height: 15px;">
                <div class="uk-flex-item-1 uk-text-right">
                    <h2 class="uk-margin-bottom-remove">
                        $
                        {{ edited_record.computed_spent | nibnut.number("0,0") }}
                    </h2>
                    <strong class="uk-text-small">Fee Spent</strong>
                </div>
                <div class="uk-flex-item-1 uk-text-right">
                    <h2 :class="{'uk-text-danger': fee_remaining < 0}" class="uk-margin-bottom-remove">
                        $
                        {{ fee_remaining | nibnut.number("0,0") }}
                    </h2>
                    <strong class="uk-text-small">Fee Remaining</strong>
                </div>
                <div class="uk-flex-item-1 uk-text-right">
                    <h2
                        :class="{ 'uk-text-danger': (edited_record.computed_fee != calculated_fee) }"
                        :data-uk-tooltip="(edited_record.computed_fee != calculated_fee) ? '{pos:\'bottom\'}' : false"
                        :title="(edited_record.computed_fee != calculated_fee) ? `The Deltek total ($ ${nibnut_filter('nibnut.number', [edited_record.computed_fee, '0,0'])}) does not match the sum of all phase totals. ($ ${nibnut_filter('nibnut.number', [calculated_fee, '0,0'])})` : false"
                        class="uk-margin-bottom-remove"
                    >
                        $
                        {{ edited_record.computed_fee | nibnut.number("0,0") }}
                    </h2>
                    <strong class="uk-text-small">Total Fee</strong>
                </div>
            </div>
        </template>
        <template v-slot:subtitle-view>
            <div
                v-if="!!edited_record && !!is_active"
                :class="{ 'uk-active': showing_scopes }"
                class="nav-scopes"
            >
                <div
                    v-for="(scope, index) in project_scopes"
                    :key="scope.id"
                    :id="scope.id"
                    :class="{ 'uk-active': (scope.id === edited_record.id), 'uk-last': (index >= (project_scopes.length - 1)), 'uk-scrollable': scope_is_scrollable(scope) }"
                    class="nav-scope"
                >
                    <div
                        class="nav-scope-identifier"
                    >
                        <router-link
                            :to="{name: 'project.edit',
                            params: { id: scope.id }}"
                        >
                            {{ scope.number_suffix }} {{ scope.nickname || scope.name }}
                        </router-link>
                    </div>
                    <div
                        data-uk-tooltip="{pos:'bottom'}"
                        :title="scope_breakdown(scope)"
                        class="nav-scope-fee"
                    >
                        <div>
                            $ {{ scope.computed_fee | nibnut.number("0,0") }}
                        </div>
                    </div><!--
                    //--><component
                        :is="(scope.id === edited_record.id) ? 'a' : 'div'"
                        v-for="phase_summary in scope.phase_summary"
                        :key="phase_summary.id"
                        data-uk-tooltip="{pos:'bottom'}"
                        :title="phase_fee_breakdown(scope, phase_summary, phase_summary.computed_projected_fee)"
                        :class="{'uk-active': phase_summary.active}"
                        class="nav-scope-phase"
                        @click.prevent="maybe_activate_phase(scope, phase_summary)"
                    >
                        <div>
                            <span>{{ phase_summary.abbreviation }}</span>
                            <span :class="{'uk-text-danger-': phase_fee_remaining(phase_summary) < 0 }">
                                $
                                {{ phase_fee_remaining(phase_summary) | nibnut.number("0,0") }}
                            </span>
                        </div>
                    </component>
                </div>
                <base-button
                    v-if="is_scoped"
                    size="mini"
                    :waiting="scopes_loading"
                    class="scoped-project-button"
                    @click.prevent="toggle_scopes"
                >
                    <open-icon :glyph="showing_scopes ? 'caret-up' : 'caret-down'" />
                    Multi-Scope  Project Fee $ {{ scope_total | nibnut.number("0,0") }}
                </base-button>
            </div>
        </template>
        <template v-slot:main-page-actions>
            <div
                v-if="has_actions"
                class="lam-main-action-button"
            >
                <dropdown-button
                    size="large"
                    data-uk-dropdown-old="{pos:'left-top'}"
                    data-uk-dropdown="{pos:'bottom-right'}"
                    class="lam-project-actions"
                >
                    <template v-slot:dropdown>
                        <ul class="uk-nav uk-nav-dropdown uk-text-left">
                            <li v-if="!is_active">
                                <a href @click.prevent="duplicate">
                                    Duplicate For a New Team
                                </a>
                            </li>
                            <li v-if="!is_active && is_comparable">
                                <a href @click.prevent="comparing=true">
                                    Compare to similar projects
                                </a>
                            </li>
                            <li v-if="!!is_active && (is_administrator || is_db_admin_active || is_developer)">
                                <a href @click.prevent="showing_deltek=true">
                                    Deltek Data...
                                </a>
                            </li>
                            <li v-if="!!is_active">
                                <a href @click.prevent="add_scope">
                                    Add a scope to this project...
                                </a>
                            </li>
                            <li v-if="!!is_active && !is_scoped">
                                <a href @click.prevent="breaking_into_scopes = true">
                                    Break this project into scopes...
                                </a>
                            </li>
                            <li v-if="!!is_active && is_scoped">
                                <a href @click.prevent="confirm_rescope">
                                    Re-scope Project...
                                </a>
                            </li>
                        </ul>
                    </template>
                    <open-icon glyph="cog" />
                </dropdown-button>
            </div>
        </template>

        <template v-slot:left-panel>
            <prospective-project
                v-if="!!edited_record&&!is_active"
                :record="edited_record"
                :editable="editable"
                :viewable="can_view_record_data"
            />
            <active-project
                v-else-if="!!edited_record"
                :record="edited_record"
                :editable="editable"
                :viewable="can_view_record_data"
                :style="{ 'margin-top': showing_scopes ? `${(edited_record.scope_sibling_ids.length * SCOPE_LINE_HEIGHT) + SCOPE_PUSH_DOWN_MARGIN_MIN}px` : `${SCOPE_PUSH_DOWN_MARGIN_MIN}px` }"
                class="project-scope-pushed"
            />

            <comparables
                :show.sync="comparing"
                :compare-to="edited_record"
            />

            <modal-dialog
                id="active-phase-change"
                :show.sync="confirm_phase_change"
            >
                <template v-slot:title>
                    End "{{ phase_standardized_title(active_phase) }}"
                </template>
                <template v-slot:subtitle>
                    Start "{{ phase_standardized_title(confirming_phase) }}"
                </template>

                <div class="uk-modal-body">
                    <div class="uk-form-controls-text uk-text-bold">
                        How was your experience with the client during this phase? (optional)
                    </div>
                    <form-text-area
                        id="new-phase-firm-comment"
                        name="comment"
                        v-model="confirming_phase_notes"
                        width="1-1"
                        size="medium"
                        :required="false"
                        class="uk-width-1-1"
                    />

                    <div
                        v-if="!!edited_record && !edited_record.open_asset_id && !edited_record.consider_for_marketing && (phase_by_id(confirming_phase.phase).code_constant === 'CA')"
                        class="uk-form-controls-text uk-text-bold uk-margin-top"
                    >
                        <form-checkbox
                            id="confirming_phase_consider_for_marketing"
                            v-model="confirming_phase_consider_for_marketing"
                            name="confirming_phase_consider_for_marketing"
                        >
                            This project should be considered for marketing purposes
                        </form-checkbox>
                    </div>
                </div>

                <div class="uk-modal-footer uk-text-center">
                    <base-button
                        @click.prevent="confirm_phase_change=false"
                    >
                        Cancel
                    </base-button>
                    <base-button
                        @click.prevent="activate_phase"
                    >
                        Proceed
                    </base-button>
                </div>
            </modal-dialog>

            <deltek-data
                :show.sync="showing_deltek"
                :project-id="edited_record ? edited_record.id : 0"
            />
            <new-project
                :show.sync="creating_project"
                :internal="!!edited_record && !!edited_record.internal"
                :source-data="edited_record"
                :scope="creating_project_as_scope"
            />
            <rescope-dialog
                :show.sync="rescoping"
                :project="edited_record"
                :fields="fields"
                :relation-ids="relation_ids"
                @scoped="maybe_toggle_scopes"
            />
            <break-into-scopes-dialog
                :show.sync="breaking_into_scopes"
                :project="edited_record"
                :fields="fields"
                :relation-ids="relation_ids"
                @scoped="maybe_toggle_scopes"
            />
        </template>
    </record-editor>
</template>

<script type="text/javascript">
import { orderBy } from "lodash"

import is_record_editor from "../../mixins/IsRecordEditor"
import project_utilities from "../../mixins/ProjectUtilities"
import phase_utilities from "../../mixins/PhaseUtilities"
import uses_color_palette from "../../mixins/UsesColorPalette"

import ModalDialog from "../../dialogs/ModalDialog/ModalDialog"
import NewProject from "../../dialogs/NewProject"
import DeltekData from "../../dialogs/DeltekData"
import RescopeDialog from "./RescopeDialog"
import BreakIntoScopesDialog from "./BreakIntoScopesDialog"
import DropdownButton from "../../ui/DropdownButton"
import FormInput from "../../ui/FormInput"
import FormTextArea from "../../ui/FormTextArea"
import FormCheckbox from "../../ui/FormCheckbox"
import BaseButton from "../../ui/BaseButton"
import OpenIcon from "../../ui/OpenIcon"

import ProspectiveProject from "./ProspectiveProject"
import ActiveProject from "./ActiveProject"
import Comparables from "./Comparables"

export default {
    mixins: [is_record_editor, project_utilities, phase_utilities, uses_color_palette],
    components: {
        ModalDialog,
        NewProject,
        DeltekData,
        RescopeDialog,
        BreakIntoScopesDialog,
        DropdownButton,
        FormInput,
        FormTextArea,
        FormCheckbox,
        BaseButton,
        OpenIcon,
        ProspectiveProject,
        ActiveProject,
        Comparables
    },
    watch: {
        $route: "reset_ui",
        "edited_record.id": "maybe_redirect"
    },
    methods: {
        reset_ui () {
            this.showing_scopes = this.is_scoped
        },
        maybe_redirect () {
            if(this.edited_record) {
                if((this.$route.name === "project.edit") && !this.edited_record.activated_at) this.$router.replace({ name: "prospective_project.edit", params: { id: this.edited_record.id } })
                else if((this.$route.name === "prospective_project.edit") && !!this.edited_record.activated_at) this.$router.replace({ name: "project.edit", params: { id: this.edited_record.id } })
                else this.reset_ui()
            }
        },
        duplicate () {
            this.creating_project_as_scope = false
            this.creating_project = true
        },
        maybe_activate_phase (scope, phase) {
            if(scope.id !== this.edited_record.id) return

            this.confirming_phase = phase
            this.confirming_phase_notes = ""
            this.confirming_phase_consider_for_marketing = false
            this.confirm_phase_change = true
            /*
            const project_phase = this.phase_by_id(phase.phase)
            if(!this.confirming_phase && project_phase && (project_phase.code_constant === 'CA') && this.edited_record && !this.edited_record.consider_for_marketing) {
                this.confirming_phase = phase
                this.confirming_phase_notes = ""
                this.confirming_phase_consider_for_marketing = false
                this.confirm_phase_change = true
            } else this.activate_phase(phase)
            */
        },
        activate_phase () {
            if(!this.confirming_phase || !this.confirming_phase.id) return

            const data = {
                active_phase_id: this.confirming_phase.id,
                closing_phase_comment: this.confirming_phase_notes,
                fields: ["id", "number", "number_suffix", "phase_summary"]
            }
            const project_phase = this.phase_by_id(this.confirming_phase.phase)
            if(project_phase && (project_phase.code_constant === "CA")) data.consider_for_marketing = this.confirming_phase_consider_for_marketing

            this.$store.dispatch("RECORD_SAVE", {
                entity: "project",
                id: this.edited_record.id,
                data
            }).then(() => {
                this.confirming_phase = {}
                this.confirming_phase_notes = ""
                this.confirming_phase_consider_for_marketing = false
                this.confirm_phase_change = false
            }).catch(error => {
                if(error) console.log(error)
            })
        },
        scope_breakdown (scope) {
            return `
                <strong>${scope.number_suffix} ${scope.nickname || scope.name}</strong><br/>
                Spent: $ ${this.nibnut_filter("nibnut.number", [scope.computed_spent, "0,0"])}<br/>
                Remaining: $ ${this.nibnut_filter("nibnut.number", [(scope.computed_fee - scope.computed_spent), "0,0"])}<br/>
                Total: $ ${this.nibnut_filter("nibnut.number", [scope.computed_fee, "0,0"])}
            `
        },
        scope_is_scrollable (scope) {
            const node = document.getElementById(scope.id)
            if(node) return node.scrollWidth > node.clientWidth
            // console.log({ scope: scope.id, scrollWidth: node.scrollWidth, clientWidth: node.clientWidth })
            return false
        },
        phase_fee_breakdown (scope, phase, projected = null) {
            if(projected === null) projected = this.phase_fee_projected(phase)
            return `
                <strong>${phase.title}</strong><br/>
                Remaining: $ ${this.nibnut_filter("nibnut.number", [this.phase_fee_remaining(phase), "0,0"])}<br/>
                Spent: $ ${this.nibnut_filter("nibnut.number", [phase.computed_spent, "0,0"])}<br/>
                Projected: $ ${this.nibnut_filter("nibnut.number", [projected, "0,0"])}<br/>
                Budgeted: $ ${this.nibnut_filter("nibnut.number", [phase.fee, "0,0"])}
            `
        },
        change_suffix (event) {
            if(event.target.value !== this.edited_record.number_suffix) this.save_field(event.target.value, "number_suffix")
        },
        toggle_scopes () {
            if(this.showing_scopes) this.showing_scopes = false
            else {
                this.scopes_loading = true
                this.$store.dispatch("FETCH_RECORDS", {
                    entity: "project",
                    query: {
                        ids: this.edited_record.scope_sibling_ids,
                        fields: ["id", "number", "number_suffix", "phase_summary"]
                    }
                }).then(() => {
                    this.showing_scopes = true
                }).catch(this.receive_error).then(() => {
                    this.scopes_loading = false
                })
            }
        },
        maybe_toggle_scopes () {
            if(!this.showing_scopes) this.toggle_scopes()
        },
        add_scope () {
            this.creating_project_as_scope = true
            this.creating_project = true
        },
        confirm_rescope () {
            this.rescoping = true
        },
        receive_error (error) {
            if(error.topic_id && (error.topic_id !== "number_suffix")) this.$set(this.errors, error.topic_id, error.message)
            else {
                this.topic_less_error(error)
                if(error.topic_id === "number_suffix") {
                    setTimeout(() => {
                        const number_suffix = this.edited_record.number_suffix
                        this.edited_record.number_suffix = ""
                        setTimeout(() => {
                            this.edited_record.number_suffix = number_suffix
                        }, 10)
                    }, 10)
                }
            }
        },
        record_deleted (record) {
            if(record.activated_at) this.$router.push({ name: "project.list" })
            else this.$router.push({ name: "prospective_project.list" })
        },
        link_open_asset () {
            this.linking_open_asset = true
            this.$store.dispatch("RECORD_ACTION", {
                entity: this.entity,
                id: this.edited_record.id,
                action: "open_asset",
                data: {},
                method: "put"
            }).catch(error => {
                this.$error(error.message)
            }).finally(() => {
                this.linking_open_asset = false
            })
        }
    },
    computed: {
        raw_contact_records () {
            return this.entity_records("contact")
        },
        raw_contact_project_records () {
            return this.entity_records("contact_project")
        },
        raw_firm_records () {
            return this.entity_records("firm")
        },
        raw_project_records () {
            return this.entity_records("project")
        },
        raw_phase_records () {
            return this.entity_records("phase")
        },
        phases () {
            if(!this.edited_record || !this.edited_record.id) return []
            return orderBy(this.raw_phase_records.filter(phase => phase.project_id === this.edited_record.id), "phase", "asc")
        },
        active_phase () {
            if(!this.edited_record || !this.edited_record.id) return {}
            return this.raw_phase_records.find(phase => phase.id === this.edited_record.active_phase_id)
        },
        calculated_fee () {
            return this.phases.reduce((total, phase) => {
                return total + phase.fee
            }, 0)
        },
        fields () {
            return [
                "fieldset::default",
                "phase_summary",
                "ns::project;fieldset::relations",
                "ns::contact_project;fieldset::default",
                "ns::contact;fieldset::default",
                "ns::firm;fieldset::selection",
                "ns::user;fieldset::project-staff",
                "ns::phase;fieldset::default",
                "ns::task;fieldset::default,working_weeks"
            ]
        },
        page_title () {
            if(this.edited_record && this.edited_record.activated_at) {
                if(this.edited_record.number && this.edited_record.name) return this.edited_record.number + " " + this.edited_record.name
                if(this.edited_record.number) return "Project #" + this.edited_record.number
                if(this.edited_record.name) return this.edited_record.name
                return "Un-named Overhead Project"
            }
            return ((this.edited_record && this.edited_record.name) ? this.edited_record.name : "Project")
        },
        top_bar_color () {
            if(this.edited_record) {
                if(this.edited_record.completed_at) return this.color_completed.background
                if(this.edited_record.project_status === "on-hold") return "#e6e1a2"
                if(this.edited_record.project_status === "dead") return "#e6a2a2"
            }
            return ""
        },
        editable () {
            const is_on_team = !!this.team_member_for_project_id(this.profile_id, this.edited_record.id)
            if(this.profile_id && this.is_active) {
                return this.edited_record.internal || (this.can_edit_record_data && (this.is_db_admin_active || this.is_associate_principal || this.is_principal || this.is_business_manager || this.is_associate || this.is_senior_associate || this.is_administrator || is_on_team))
            }
            return this.can_edit_record_data || is_on_team
        },
        main_contact_project () {
            if(!this.edited_record || !this.edited_record.id || !this.raw_contact_project_records.length) return null
            return this.raw_contact_project_records.find(contact_project => (contact_project.internal_category === "main") && (contact_project.project_id === this.edited_record.id))
        },
        main_contact () {
            const main_contact_project = this.main_contact_project
            if(main_contact_project) {
                return this.raw_contact_records.find(contact => contact.id === main_contact_project.contact_id)
            }
            return null
        },
        main_firm () {
            const main_contact_project = this.main_contact_project
            if(main_contact_project) {
                return this.raw_firm_records.find(firm => firm.id === main_contact_project.firm_id)
            }
            return null
        },
        pic () {
            return this.pic_for_project(this.edited_record)
        },
        pm () {
            return this.pm_for_project(this.edited_record)
        },
        is_active () {
            return this.project_is_active(this.edited_record)
        },
        raw_project_scopes () {
            if(!this.edited_record) return []
            return this.raw_project_records.filter(project => project.number === this.edited_record.number)
        },
        project_scopes () {
            return orderBy(this.raw_project_scopes, "number_suffix", "asc")
        },
        scope_total () {
            let total = 0
            total = this.raw_project_scopes.reduce((total, project) => {
                return total + project.computed_fee
            }, 0)
            return Math.round(total)
        },
        related_projects () {
            if(!!this.edited_record && !!this.edited_record.relationship_id) {
                return this.raw_project_records.filter(project => (project.id !== this.edited_record.id) && (project.relationship_id === this.edited_record.relationship_id))
            }
            return []
        },
        is_comparable () {
            if(!this.edited_record) return false
            const accepted_proposal = this.project_has_accepted_proposal(this.edited_record.id)
            return this.edited_record && !this.edited_record.activated_at && this.edited_record.size && this.edited_record.category_ids.length && !!accepted_proposal
        },
        fee_remaining () {
            if(!this.edited_record) return 0
            return this.edited_record.computed_fee - this.edited_record.computed_spent
        },
        has_actions () {
            if(this.is_active) return !!this.edited_record && !!this.edited_record.number && ((this.is_administrator || this.is_db_admin_active || this.is_developer) || this.is_scoped)
            return true
        },
        is_scoped () {
            return this.is_active && !!this.edited_record && !!this.edited_record.scope_sibling_ids && !!this.edited_record.scope_sibling_ids.length
        }
    },
    data () {
        return {
            entity: "project",
            relation_ids: ["contact", "contact_project", "firm", "user", "project", "project_user", "attachment", "phase", "task", "comment", "user_preference", "project_log", "address"],

            creating_project: false,
            creating_project_as_scope: null,
            comparing: false,

            rescoping: false,

            confirm_phase_change: false,
            confirming_phase: {},
            confirming_phase_notes: "",
            confirming_phase_consider_for_marketing: false,

            showing_deltek: false,

            showing_scopes: false,
            scopes_loading: false,

            breaking_into_scopes: false,

            linking_open_asset: false,

            SCOPE_LINE_HEIGHT: 33,
            SCOPE_PUSH_DOWN_MARGIN_MIN: 50
        }
    }
}
</script>

<style lang="less">
@import "@/assets/less/variables";

.lam-page-title {
    h1 .uk-button-large {
        &.uk-inert-button {
            font-size: 26px;
        }
    }
    h2.project-title {
        text-transform: none;
    }
    .uk-button-dropdown {
        .uk-button {
            padding: 0 5px;
            background: transparent;
            color: #60605B;
            min-height: 38px;
            line-height: 38px;
        }
        .uk-dropdown {
            line-height: 1rem;
            padding-top: 1em;
            padding-bottom: 1em;
            background-color: white;
            box-shadow: 0 1px 1px lighten(@global-color, 25%);
        }
    }
}

.lam-main-action-button {
    position: absolute;
    right: 0;
    top: -3px;
}
.lam-title-separator {
    border-right: 1px solid #d0d0d0;
    align-items: flex-end;

    input.uk-form-small {
        font-size: inherit;
        height: 1.2em;
        color: @global-lam-blue;

        &[disabled] {
            padding: 0;
            color: @global-lam-blue;
        }
    }
}

.lam-page-subtitle {
    position: relative;
    scrollbar-width: thin;
    scrollbar-color: @global-lam-orange @global-lam-gray;

    ::-webkit-scrollbar {
        width: 3px;
        height: 3px;
    }
    ::-webkit-scrollbar-track {
        background-color: @global-lam-gray;
    }
    ::-webkit-scrollbar-thumb {
        background-color: @global-lam-orange;
        border-radius: 2px;
        border: 0px;
    }

    @identifier-width: 200px;
    .nav-scopes {
        overflow-x: auto;
        margin-left: -1em;
        margin-right: -1em;
        font-weight: bold;

        @scope-height: 25px;
        @scope-active-height: 45px;
        .nav-scope {
            position: relative;
            padding-left: @identifier-width;
            border-bottom: 0;
            white-space: nowrap;
            overflow-y: hidden;
            height: 0;
            transition: all .2s cubic-bezier(0.4,0,0.2,1);

            & > * {
                height: @scope-height;
                line-height: @scope-height;
            }
            .nav-scope-identifier {
                position: absolute;
                top: 0;
                left: 0;
                padding-left: 10px;
                padding-right: 10px;
                width: @identifier-width;
                opacity: 1;
                box-sizing: border-box;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                transition: all .2s cubic-bezier(0.4,0,0.2,1);
                pointer-events: auto;

                &:hover {
                    a {
                        text-decoration: none;
                    }
                }
            }
            .nav-scope-phase,
            .nav-scope-fee {
                display: inline-block;
                padding-left: 20px;
                padding-right: 20px;
                border-right: 1px solid #dededc;
                width: 150px;
                box-sizing: border-box;

                & > div {
                    display: flex;
                    flex-direction: row;
                    flex-wrap: nowrap;

                    & > span:first-child {
                        flex: 1 1 auto;
                        overflow: hidden;
                        white-space: nowrap;
                        text-overflow: ellipsis;
                    }
                    & > span:last-child {
                        flex: 0 0 70px;
                        // text-align: right;
                    }
                }

                &.uk-active {
                    font-weight: bold;
                }

                &:hover {
                    background-color: @global-lam-dusk;

                    a {
                        text-decoration: none;
                    }
                }
            }
            .nav-scope-fee {
                padding-left: 10px;
                padding-right: 10px;
                border-left: 3px double @global-lam-gray;
                border-right: 3px double @global-lam-gray;
                width: 100px;
                font-style: italic;
            }

            &.uk-active {
                height: @scope-height;
                .nav-scope-identifier {
                    background-color: @global-lam-blue;

                    &, a, a:hover, a:active, a:focus, a:disabled {
                        color: white;
                    }
                }
                .nav-scope-phase {
                    &.uk-active {
                        background-color: @global-lam-orange;
                        color: white;

                    }
                }

                &:hover {
                    a {
                        text-decoration: none;
                    }
                }
            }
            &.uk-scrollable {
                height: @scope-active-height;
                & > * {
                    height: @scope-active-height;
                }
            }
        }

        &.uk-active {
            .nav-scope {
                height: @scope-height;
                border-bottom: 2px solid #dededc;

                &.uk-last {
                    border-bottom: 0;
                }
                &.uk-scrollable {
                    height: @scope-active-height;
                }
            }
        }
    }
    .scoped-project-button {
        position: absolute;
        top: 100%;
        left: 0;
        width: @identifier-width;
    }
}
.project-scope-pushed {
    transition: margin-top .2s cubic-bezier(0.4,0,0.2,1);
}

.lam-project-contacts {
    position: relative;

    .lam-related-projects-button {
        position: absolute;
        top: 50%;
        right: 0;
        transform: translate(0, -50%;);

        .uk-button {
            min-height: 0;
            line-height: normal;
        }
    }
}
</style>
